import i18n from "i18next";
import ICU from "i18next-icu";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import resourcesToBackend from "i18next-resources-to-backend";
import * as Sentry from "@sentry/react";
import { SUPPORTED_LANGUAGES } from "./languages";
import { pseudoLocalizeBundle } from "./pseudoLocalize";

const defaultNS = "common" as const;

const RTL_LANGS = new Set(["ar", "he", "fa", "ur"]);
const dirFor = (lng: string | undefined): "rtl" | "ltr" => (RTL_LANGS.has(lng?.split("-")[0] ?? "") ? "rtl" : "ltr");

// The UI language is an account-wide preference (follows the user across devices), not
// device-local. The dev-only `en-XA` pseudo-locale is never persisted. Flow:
// - Settings: LanguageSelector / LanguageSwitcher call i18n.changeLanguage() and
//   PATCH /api/user_preferences/ { language } via useUpdateLanguagePreference.
// - Signup: SignUpForm seeds it from i18n.resolvedLanguage in the registerOrganization payload.
// - Login/reload: tokenLogin reads the top-level `language` from the userprofile response and
//   calls i18n.changeLanguage() so the account preference wins over the local cache.
//
// localStorage `i18nextLng` (managed by i18next-browser-languagedetector) remains the
// bootstrap/offline source of truth until the account preference is loaded.

void i18n
  .use(ICU)
  .use(
    resourcesToBackend(async (lng: string, ns: string) => {
      const isPseudo = lng === "en-XA";
      const sourceLng = isPseudo ? "en" : lng;
      const mod = (await import(`../locales/${sourceLng}/${ns}.json`)) as {
        default: unknown;
      };
      return isPseudo ? pseudoLocalizeBundle(mod.default) : mod;
    }),
  )
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    defaultNS,
    enableSelector: "strict",
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LANGUAGES,
    interpolation: { escapeValue: false },
    keySeparator: "\x1F",
    nsSeparator: "\x1E",
    returnNull: false,
    returnEmptyString: false,
    saveMissing: import.meta.env.DEV,
    missingKeyHandler:
      import.meta.env.DEV ?
        (lngs, ns, key) => {
          // eslint-disable-next-line no-console
          console.warn(`[i18n] missing ${lngs.join(",")}:${ns}:${key}`);
        }
      : undefined,
    parseMissingKeyHandler: import.meta.env.DEV ? (key, fallbackValue) => fallbackValue ?? `❮${key}❯` : undefined,
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
      lookupLocalStorage: "i18nextLng",
    },
    react: { useSuspense: true },
  });

i18n.on("languageChanged", (lng) => {
  document.documentElement.lang = lng;
  document.documentElement.dir = dirFor(lng);
  Sentry.setTag("locale", lng);
});

export default i18n;
